/**
 * Conversions (`/conversions`) — pages and outcomes ranked by what
 * they win for the site owner, whatever the vertical: revenue for a
 * shop, reader value for a publication, completed services for a
 * council. Replaces the old commerce-only `/sales` scaffold: sales is
 * one preset of the conversion-first model, not the frame.
 *
 * Thin route in the Traffic mould — state (lens, editing) + the data
 * seam, composing the placeholder-pattern surfaces from
 * `components/conversions/ConversionsPlaceholders.tsx`:
 *
 *   banner (first-run preset ask / declare-kinds) → KPI strip →
 *   trend → kinds + pages cards
 *
 * The template is read-layer config (localStorage today — see
 * use-template.ts for the durable-home plan). Until a template is
 * saved, the board previews the commerce preset so the surface always
 * looks like the dashboard it is; picking a pill on the banner saves
 * it. Conversion data is per-preset sample fixtures behind
 * `useConversionData` pending the `conversion_events` read-model
 * (contract: `packages/shared/src/conversions.ts`, plans/1.1.0/ingest).
 *
 * Sits as a peer of Traffic, Performance, and Behaviours so the IA
 * reads as four lenses on the same site: who, how fast, what, and
 * what it wins you.
 */
import {
  ConversionsKindsCard,
  ConversionsKpiRow,
  type ConversionsLens,
  ConversionsPagesCard,
  ConversionsSetupBanner,
  ConversionsTrendChart,
  conversionMetric,
} from '@/components/conversions/ConversionsPlaceholders';
import { ConversionsSetup } from '@/components/conversions/ConversionsSetup';
import { useConversionData } from '@/components/conversions/use-conversion-data';
import { templateFromPreset, useConversionsTemplate } from '@/components/conversions/use-template';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { useActiveSiteUuid } from '@/lib/active-site';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/conversions/')({
  // Disabled surface — redirect any direct access to /performance.
  beforeLoad: () => {
    throw redirect({ to: '/performance' });
  },
  component: ConversionsPage,
});

function ConversionsPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  return (
    <SiteIsland section="Conversions">
      {siteUuid && <ConversionsBody siteUuid={siteUuid} />}
    </SiteIsland>
  );
}

function ConversionsBody({ siteUuid }: { siteUuid: string }): ReactElement {
  const { template: stored, saveTemplate } = useConversionsTemplate(siteUuid);
  const [editing, setEditing] = useState(false);
  const [lens, setLens] = useState<ConversionsLens>('value');

  // Until the site saves a template, preview the commerce preset so
  // the surface reads as the dashboard it is (the banner carries the
  // "pick your site type" ask); saving swaps the vocabulary in place.
  const template = stored ?? templateFromPreset('commerce');
  const saved = stored !== null;

  const { pages, daily, isLoading } = useConversionData(siteUuid);

  if (editing) {
    return (
      <div className="space-y-6">
        <PageIntro />
        <ConversionsSetup
          initial={template}
          onDone={(next) => {
            saveTemplate(next);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  const kpis = pages
    ? {
        value: pages.reduce((s, p) => s + conversionMetric(p, 'value', template), 0),
        count: pages.reduce((s, p) => s + conversionMetric(p, 'count', template), 0),
        pageviews: pages.reduce((s, p) => s + p.pageviews, 0),
      }
    : undefined;

  return (
    <div className="space-y-6">
      <PageIntro />
      <ConversionsSetupBanner
        saved={saved}
        hasKinds={template.kinds.length > 0}
        preset={template.preset}
        onPickPreset={(preset) => saveTemplate(templateFromPreset(preset))}
        onDeclare={() => setEditing(true)}
      />
      <ConversionsKpiRow data={kpis} trend={daily} template={template} />
      <ConversionsTrendChart
        data={daily}
        loading={isLoading}
        lens={lens}
        onLensChange={setLens}
        template={template}
      />
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]">
        <ConversionsKindsCard pages={pages} template={template} onEdit={() => setEditing(true)} />
        <ConversionsPagesCard pages={pages} template={template} lens={lens} />
      </div>
    </div>
  );
}

function PageIntro(): ReactElement {
  return (
    <div className="space-y-1">
      <h1 className="text-base font-semibold text-[var(--color-ink)]">Conversions</h1>
      <p className="max-w-prose text-sm text-[var(--color-mute)]">
        Pages ranked by the wins they drive — purchases, signups, completed services — weighted your
        way.
      </p>
    </div>
  );
}
